/**
 * @sazeo/sdk — Calendar hooks
 *
 * Minimal hooks that call the `calendar` Edge Function. All requests carry the
 * caller's Supabase JWT. RLS enforces tenant isolation.
 *
 * This module mirrors the lightweight data-fetching primitive used by
 * documents.ts — `{ data, isLoading, error, refetch }` / `{ mutate, isPending, error }`
 * so UI code stays consistent across modules.
 *
 * Paired spec: specs/CODEX_PHASE2_WAVE4_CALENDAR_TASKS_HOME.md §2.4 / §3.1
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type AttendeeRole = 'organizer' | 'required' | 'optional';
export type AttendeeResponse = 'pending' | 'accepted' | 'declined' | 'tentative';
export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export interface EventRow {
  id: string;
  tenant_id: string;
  created_by: string;
  title: string;
  description: string | null;
  source_lang: string;
  location: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  timezone: string;
  recurrence_rule: string | null;
  recurrence_until: string | null;
  parent_event_id: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

/** Expanded occurrence — one per generated instance of a recurring event. */
export interface EventOccurrence extends EventRow {
  /** ISO string of this specific occurrence start time. */
  occurrence_start: string;
  occurrence_end: string;
  /** Server returns these alternative field names — both are supported. */
  occurrence_starts_at?: string;
  occurrence_ends_at?: string;
  /** ID of the master (series root) event for this occurrence. */
  master_event_id?: string;
}

export interface EventAttendeeRow {
  event_id: string;
  user_id: string;
  tenant_id: string;
  role: AttendeeRole;
  response: AttendeeResponse;
  responded_at: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
}

export interface EventCreateInput {
  title: string;
  description?: string | null;
  location?: string | null;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  timezone?: string;
  recurrence_rule?: string | null;
  recurrence_until?: string | null;
  color?: string | null;
  attendee_user_ids?: string[];
}

export type EventUpdateInput = Partial<EventCreateInput>;

// ---------------------------------------------------------------------------
// Internal fetch helpers (mirror documents.ts pattern)
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return {
      auth: {
        getSession: async () => ({ data: { session: null } }),
      },
    };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

async function apiDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, { method: 'DELETE', headers });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
}

// ---------------------------------------------------------------------------
// Query primitive (same shape as documents.ts's useAsyncQuery)
// ---------------------------------------------------------------------------

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export interface EventRange {
  from: string; // ISO
  to: string; // ISO
}

/** List events with recurrence occurrences expanded server-side. */
export function useEvents(range: EventRange) {
  return useAsyncQuery<EventOccurrence[]>(
    () => {
      const qs = new URLSearchParams({ from: range.from, to: range.to }).toString();
      return apiFetch<EventOccurrence[]>(`calendar/events?${qs}`);
    },
    [],
    [range.from, range.to],
  );
}

export function useEvent(eventId: string | null) {
  return useAsyncQuery<EventRow | null>(
    () => (eventId ? apiFetch<EventRow>(`calendar/events/${eventId}`) : Promise.resolve(null)),
    null,
    [eventId],
    Boolean(eventId),
  );
}

export function useCreateEvent() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: EventCreateInput) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<EventRow>('calendar/events', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error };
}

export function useUpdateEvent() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { event_id: string; patch: EventUpdateInput }) => {
    setIsPending(true);
    try {
      return await apiFetch<EventRow>(`calendar/events/${input.event_id}`, {
        method: 'PATCH',
        body: JSON.stringify(input.patch),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useDeleteEvent() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (eventId: string) => {
    setIsPending(true);
    try {
      await apiDelete(`calendar/events/${eventId}`);
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useAttendees(eventId: string | null) {
  return useAsyncQuery<EventAttendeeRow[]>(
    () =>
      eventId
        ? apiFetch<EventAttendeeRow[]>(`calendar/events/${eventId}/attendees`)
        : Promise.resolve([]),
    [],
    [eventId],
    Boolean(eventId),
  );
}

export function useSetAttendance() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (input: { event_id: string; response: AttendeeResponse }) => {
    setIsPending(true);
    try {
      return await apiFetch<EventAttendeeRow>(`calendar/events/${input.event_id}/my-response`, {
        method: 'PATCH',
        body: JSON.stringify({ response: input.response }),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

// ---------------------------------------------------------------------------
// Recurring event mutation hooks
// ---------------------------------------------------------------------------

export interface OccurrenceInput {
  eventId: string;
  occurrenceDate: string; // YYYY-MM-DD
}

export type OccurrenceEditInput = OccurrenceInput & Partial<EventCreateInput>;

/** Cancel a single occurrence of a recurring event. */
export function useCancelOccurrence() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: OccurrenceInput) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<Record<string, unknown>>(
        `calendar/events/${input.eventId}/cancel-occurrence`,
        { method: 'PATCH', body: JSON.stringify({ occurrence_date: input.occurrenceDate }) },
      );
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error };
}

/** Edit a single occurrence of a recurring event (creates an exception override row). */
export function useEditOccurrence() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: OccurrenceEditInput) => {
    setIsPending(true);
    setError(null);
    try {
      const { eventId, occurrenceDate, ...patch } = input;
      return await apiFetch<EventRow>(`calendar/events/${eventId}/edit-occurrence`, {
        method: 'PATCH',
        body: JSON.stringify({ occurrence_date: occurrenceDate, ...patch }),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error };
}

/** Edit this and all following occurrences — truncates the original series and creates a new master. */
export function useEditThisAndFollowing() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(async (input: OccurrenceEditInput) => {
    setIsPending(true);
    setError(null);
    try {
      const { eventId, occurrenceDate, ...patch } = input;
      return await apiFetch<EventRow>(`calendar/events/${eventId}/edit-this-and-following`, {
        method: 'PATCH',
        body: JSON.stringify({ occurrence_date: occurrenceDate, ...patch }),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending, error };
}
